import * as THREE from 'three'
import Experience from '../Experience.js'
import Hammer from 'hammerjs'
import FocusToggle, { supportsTouchInput } from './FocusToggle.js'

export default class Interaction {

    static SPACE_FLIP_INITIAL_DELAY_MS = 260;
    static SPACE_FLIP_REPEAT_DELAY_MS = 340;

    static PAN_THRESHOLD_PX = 5;
    static TAP_MAX_PRESS_MS = 250;
    static TAP_MAX_MOVEMENT_PX = 9;
    static DOUBLE_TAP_INTERVAL_MS = 300;
    static DOUBLE_TAP_POSITION_THRESHOLD_PX = 10;
    static PINCH_FOCUS_SCALE_THRESHOLD = 0.12;
    static CURSOR_BROADCAST_THROTTLE_MS = 50;

    constructor() {
        this.experience = new Experience();
        this.canvas = this.experience.canvas;
        this.space = this.experience.space;
        this.behavior = this.experience.behavior;
        this.resizer = this.experience.resizer;
        this.hasFocus = true;
        this.spaceHeld = false;
        this.spaceFlipTimeout = null;
        this.spaceFlipInProgress = false;
        this.spaceFlipCount = 0;
        this.cameraIsPanning = false;
        this.InputIsDrag = false;
        this.panStart = new THREE.Vector2();
        this.lastDroppedCard = null;
        this.pinchTarget = null;
        this.pinchHandled = false;

        this.dragOffset = new THREE.Vector2();
        this.scrollDirection = null;
        this.lastScrollDirection = null;

        this._lastCursorBroadcast = 0;
        this._cursorThrottleMs = Interaction.CURSOR_BROADCAST_THROTTLE_MS;

        this.preventBrowserPinchBound = this.preventBrowserPinch.bind(this);
        this.preventBrowserZoomBound = this.preventBrowserZoom.bind(this);
        // UI toggle for focus
        this.focusToggleChangedBound = this.onFocusToggleChanged.bind(this);
        this.syncFocusToggleStateBound = this.syncFocusToggleStateFromSpace.bind(this);
        this.focusToggle = new FocusToggle(this.experience.eventBus);
        this.experience.eventBus.on('focusToggleChanged', this.focusToggleChangedBound);
        this.experience.eventBus.on('cardStateChanged', this.syncFocusToggleStateBound);
        this.syncFocusToggleStateFromSpace();

        // Input handling using explicit Hammer recognizers aligned with the
        // official single-tap/double-tap pattern.
        this.hammer = this.setupHammer();

        this.canvas.addEventListener('mousemove', this.mousemove.bind(this));
        this.canvas.addEventListener('wheel', this.scrollWheel.bind(this), { passive: true });
        this.canvas.addEventListener('wheel', this.preventBrowserZoomBound, { passive: false });
        this.canvas.addEventListener('touchmove', this.preventBrowserPinchBound, { passive: false });
        this.canvas.addEventListener('gesturestart', this.preventBrowserPinchBound);
        this.canvas.addEventListener('gesturechange', this.preventBrowserPinchBound);
        this.canvas.addEventListener('gestureend', this.preventBrowserPinchBound);
        document.addEventListener('keydown', this.keydown.bind(this));
        document.addEventListener('keyup', this.keyup.bind(this));
        window.addEventListener('blur', this.windowBlur.bind(this));
        window.addEventListener('focus', this.windowFocus.bind(this));
    }

    setupHammer() {
        const hammer = new Hammer.Manager(this.canvas);

        const doubletap = new Hammer.Tap({
            event: 'doubletap',
            taps: 2,
            interval: Interaction.DOUBLE_TAP_INTERVAL_MS,
            time: Interaction.TAP_MAX_PRESS_MS,
            threshold: Interaction.TAP_MAX_MOVEMENT_PX,
            posThreshold: Interaction.DOUBLE_TAP_POSITION_THRESHOLD_PX,
        });

        const tap = new Hammer.Tap({
            event: 'tap',
            taps: 1,
            time: Interaction.TAP_MAX_PRESS_MS,
            threshold: Interaction.TAP_MAX_MOVEMENT_PX,
        });

        const pan = new Hammer.Pan({
            event: 'pan',
            threshold: Interaction.PAN_THRESHOLD_PX,
            direction: Hammer.DIRECTION_ALL,
        });

        const pinch = new Hammer.Pinch({
            event: 'pinch',
            enable: true,
        });

        hammer.add([doubletap, tap, pan, pinch]);

        // Official Hammer pattern: allow doubletap to recognize together with
        // tap, while preventing tap from firing if doubletap succeeds.
        doubletap.recognizeWith(tap);
        tap.requireFailure(doubletap);

        hammer.on('tap', this.tap.bind(this));
        hammer.on('doubletap', this.doubletap.bind(this));
        hammer.on('panstart', this.panstart.bind(this));
        hammer.on('panmove', this.panmove.bind(this));
        hammer.on('panend', this.panend.bind(this));
        hammer.on('pinchstart', this.pinchstart.bind(this));
        hammer.on('pinchmove', this.pinchmove.bind(this));
        hammer.on('pinchend pinchcancel', this.pinchend.bind(this));

        return hammer;
    }

    mousemove(event) {
        if (!this.hasFocus) return;
        const position = this.resizer.mapClientPos({ x: event.clientX, y: event.clientY });
        const card = this.space.intersectCard(position);
        this.behavior.onMouseMove(card, position);
        this._broadcastCursor(position);
    }

    tap(event) {
        this.singletap(event);
    }

    singletap(event) {
        const position = this.resizer.mapClientPos(event.center);
        const card = this.space.intersectCard(position);
        this.behavior.onTap(card, position);
    }

    doubletap(event) {
        const position = this.resizer.mapClientPos(event.center);
        const card = this.space.intersectCard(position);
        this.behavior.onDoubleTap(card, position);
    }

    panstart(event) {
        const position = this.resizer.mapClientPos(event.center);
        const card = this.space.intersectCard(position);
        const result = this.behavior.onPanStart(card, position);

        if (result === 'camera_pan') {
            this.cameraIsPanning = true;
            this.panStart.set(event.center.x, event.center.y);
            return;
        }

        if (!result) return;

        // Capture offset between card center and cursor so the card doesn't snap
        const selectedCard = this.space.selectedCard;
        if (selectedCard) {
            const tablePos = this.space.intersectTablePos(position);
            if (tablePos) {
                this.dragOffset.set(
                    selectedCard.position.x - tablePos.x,
                    selectedCard.position.y - tablePos.y
                );
            }
        }
        this.InputIsDrag = true;
        this._broadcastCursor(position);
    }

    panmove(event) {
        // 2D pan: move camera X/Y
        if (this.cameraIsPanning) {
            const camera = this.experience.camera.instance;
            const scale = camera.position.z * 0.0015;
            var dx = (event.center.x - this.panStart.x) * scale;
            var dy = (event.center.y - this.panStart.y) * scale;
            camera.position.x = THREE.MathUtils.clamp(camera.position.x - dx, -4.2, 4.2);
            camera.position.y = THREE.MathUtils.clamp(camera.position.y + dy, -0.4, 0.4);
            this.panStart.set(event.center.x, event.center.y);
            return;
        }

        const position = this.resizer.mapClientPos(event.center);
        this.behavior.onPanMove(null, position);
        this._broadcastCursor(position);
    }

    panend(event) {
        if (this.cameraIsPanning) {
            this.cameraIsPanning = false;
            const camera = this.experience.camera.instance;
            this.experience.eventBus.pushLocal('camera_panned', {
                camera_x: camera.position.x,
                camera_y: camera.position.y
            });
            return;
        }

        const position = this.resizer.mapClientPos(event.center);
        this.behavior.onPanEnd(null, position);
    }

    pinchstart(event) {
        if (!this.behavior?.canUsePinchFocus?.()) return;

        const position = this.resizer.mapClientPos(event.center);
        this.pinchTarget = this.space.focusedCard || this.space.intersectCard(position) || null;
        this.pinchHandled = false;
    }

    pinchmove(event) {
        if (!this.pinchTarget || this.pinchHandled) return;

        const threshold = Interaction.PINCH_FOCUS_SCALE_THRESHOLD;

        if (event.scale >= 1 + threshold && !this.space.focusedCard) {
            this.lastDroppedCard = null;
            this.space.focusCard(this.pinchTarget);
            this.syncFocusToggleStateFromSpace();
            this.pinchHandled = true;
        } else if (event.scale <= 1 - threshold && this.space.focusedCard) {
            this.setLastDroppedCard(this.space.focusedCard);
            this.space.defocusAnyCard();
            this.syncFocusToggleStateFromSpace();
            this.pinchHandled = true;
        }
    }

    pinchend() {
        this.pinchTarget = null;
        this.pinchHandled = false;
    }

    preventBrowserZoom(event) {
        if (event.ctrlKey) event.preventDefault();
    }

    preventBrowserPinch(event) {
        if (event.type.startsWith('gesture')) {
            event.preventDefault();
            return;
        }
        if (event.touches?.length > 1) {
            event.preventDefault();
        }
    }

    getCurrentFlipTarget() {
        return this.space.focusedCard || this.space.selectedCard || this.space.highlightedCard;
    }

    stopSpaceFlipLoop() {
        if (this.spaceFlipTimeout) {
            clearTimeout(this.spaceFlipTimeout);
            this.spaceFlipTimeout = null;
        }
        this.spaceFlipInProgress = false;
        this.spaceFlipCount = 0;
    }

    scheduleNextSpaceFlip() {
        if (!this.spaceHeld) return;
        const delay = this.spaceFlipCount <= 1
            ? Interaction.SPACE_FLIP_INITIAL_DELAY_MS
            : Interaction.SPACE_FLIP_REPEAT_DELAY_MS;

        this.spaceFlipTimeout = setTimeout(() => {
            this.spaceFlipTimeout = null;
            this.performSpaceFlip();
        }, delay);
    }

    performSpaceFlip() {
        if (!this.spaceHeld || this.spaceFlipInProgress) return;

        const card = this.getCurrentFlipTarget();
        if (!card) {
            this.stopSpaceFlipLoop();
            return;
        }

        this.spaceFlipInProgress = true;
        this.space.flipCard(card, () => {
            this.spaceFlipInProgress = false;
            this.spaceFlipCount += 1;
            this.scheduleNextSpaceFlip();
        });
    }

    keydown(event) {
        if (event.repeat) return;
        this.behavior.onKeyDown(null, event);
    }

    keyup(event) {
        this.behavior.onKeyUp(event);
    }

    scrollWheel(event) {
        this.scrollDirection = event.deltaY > 0 ? 'down' : 'up';
        if (this.scrollDirection != this.lastScrollDirection) {
            this.behavior.onScrollWheel(null, this.scrollDirection);
        }
        this.lastScrollDirection = this.scrollDirection;
    }

    windowBlur() {
        this.hasFocus = false;
        this.behavior.onWindowBlur();
    }

    windowFocus() {
        this.hasFocus = true;
    }

    onFocusToggleChanged(event) {
        if (this.behavior?.canUseFocusToggle && !this.behavior.canUseFocusToggle()) {
            if (this.focusToggle) {
                this.focusToggle.setEnabled(false, false);
                this.focusToggle.setState(false, false);
            }
            return;
        }

        const isEnabled = !!event?.detail?.checked;

        if (isEnabled) {
            if (this.space.focusedCard) {
                this.syncFocusToggleStateFromSpace();
                return;
            }

            const targetCard = this.getFocusToggleTargetCard();
            if (!targetCard) {
                if (this.focusToggle) this.focusToggle.setState(false, false);
                return;
            }

            this.lastDroppedCard = null;
            this.space.focusCard(targetCard);
            return;
        }

        if (this.space.focusedCard) {
            this.setLastDroppedCard(this.space.focusedCard);
            this.space.defocusAnyCard();
        }
    }

    syncFocusToggleStateFromSpace() {
        if (!this.focusToggle) return;

        if (this.behavior?.canUseFocusToggle && !this.behavior.canUseFocusToggle()) {
            this.focusToggle.setEnabled(false, false);
            this.focusToggle.setState(false, false);
            return;
        }

        const canToggle = !!this.space.focusedCard || !!this.getFocusToggleTargetCard();
        const shouldBeChecked = !!this.space.focusedCard;

        this.focusToggle.setEnabled(canToggle, false);
        if (this.focusToggle.getState() !== shouldBeChecked) {
            this.focusToggle.setState(shouldBeChecked, false);
        }
    }

    getFocusToggleTargetCard() {
        return this.space.selectedCard
            || this.space.highLiftedCard
            || this.space.highlightedCard
            || (supportsTouchInput() ? this.lastDroppedCard : null);
    }

    setLastDroppedCard(card) {
        this.lastDroppedCard = supportsTouchInput() ? (card || null) : null;
    }

    destroy() {
        this.stopSpaceFlipLoop();
        this.canvas.removeEventListener('wheel', this.preventBrowserZoomBound);
        this.canvas.removeEventListener('touchmove', this.preventBrowserPinchBound);
        this.canvas.removeEventListener('gesturestart', this.preventBrowserPinchBound);
        this.canvas.removeEventListener('gesturechange', this.preventBrowserPinchBound);
        this.canvas.removeEventListener('gestureend', this.preventBrowserPinchBound);
        this.experience.eventBus.off('focusToggleChanged', this.focusToggleChangedBound);
        this.experience.eventBus.off('cardStateChanged', this.syncFocusToggleStateBound);
        if (this.focusToggle) this.focusToggle.destroy();
        this.focusToggle = null;
        this.hammer.destroy();
    }

    _broadcastCursor(ndcPosition) {
        const now = performance.now();
        if (now - this._lastCursorBroadcast < this._cursorThrottleMs) return;
        this._lastCursorBroadcast = now;

        const tablePos = this.space.intersectTablePos(ndcPosition);
        if (!tablePos) return;

        this.experience.eventBus.pushRemote('cursor_move', {
            x: tablePos.x,
            y: tablePos.y,
            z: tablePos.z
        });
    }
}
